// React
import { useState, useEffect } from "react";
// Third-Party
import { Box, Palette, Stack, Typography } from "@mui/material";
import { useSelector, useDispatch } from "@redux/store";
import { useSnackbar } from "notistack";

// Internal
import ConfirmDialog from "@components/ConfirmDialog";
import {
  disconnectOdoo as disconnectOdooAction,
  setOdooConnection,
  setOdooSyncStatus,
} from "@redux/slices/askDostChat";
import { useSyncProgress } from "@pages/askDost/hooks/useSyncProgress";
import { disconnectErp, getErpConnectionStatus, syncErp } from "@pages/askDost/services/askDostService";
import { extractSyncedEntities, extractSyncErrors } from "@pages/askDost/utils/utils";
import { ISyncedEntity } from "@pages/askDost/types";
import { AskDostOdooConnectorRow } from "../odooDialog/AskDostOdooConnectorRow";
import { AskDostOdooConnectDialog } from "../odooDialog/AskDostOdooConnectDialog";
import { AskDostSyncProgressModal } from "../odooDialog/AskDostSyncProgressModal";


interface IAskDostIntegrationsTabProps {
  palette: Palette;
  translate: (text: string, options?: any) => string;
}

export const AskDostIntegrationsTab: React.FC<IAskDostIntegrationsTabProps> = ({
  palette,
  translate,
}) => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();

  const odoo = useSelector((state: any) => state.askDostChat.odoo);
  const odooSyncStatus = useSelector(
    (state: any) => state.askDostChat.odooSyncStatus,
  );
  const [connectOpen, setConnectOpen] = useState(false);
  const [disconnectDialogOpen, setDisconnectDialogOpen] = useState(false);
  const syncProgress = useSyncProgress({ translate });

  const isSyncing = odooSyncStatus === "syncing";

  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const res = await getErpConnectionStatus();
        if (res?.data && res.data.connected && !odoo) {
          const errors = extractSyncErrors(res.data);
          errors.forEach((err) =>
            enqueueSnackbar(err, {
              variant: "warning",
              autoHideDuration: 7000,
            }),
          );

          dispatch(
            setOdooConnection({
              host: res.data.url || "Odoo Server",
              user: res.data.username || "Connected User",
              syncedAt: translate("recently_synced"),
              syncedEntities: extractSyncedEntities(res.data, translate),
            }),
          );
        }
      } catch (error) {
        console.error("Error fetching ERP status:", error);
      }
    };
    fetchStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSync = async () => {
    if (isSyncing || !odoo) return;
    dispatch(setOdooSyncStatus("syncing"));
    syncProgress.start(false, handleSync);

    try {
      const res = await syncErp(syncProgress.onProgress);
      if (res?.status === 200 || res?.status === 201) {
        const entities = extractSyncedEntities(res?.data, translate);
        const errors = extractSyncErrors(res?.data);

        dispatch(
          setOdooConnection({
            ...odoo,
            syncedAt: translate("just_now"),
            syncedEntities: entities,
          }),
        );
        syncProgress.resolve(entities);
        enqueueSnackbar(translate("erp_synced_successfully"), {
          variant: "success",
        });

        errors.forEach((err) => {
          enqueueSnackbar(err, { variant: "warning", autoHideDuration: 7000 });
        });
      } else {
        syncProgress.resolve(null);
        enqueueSnackbar(translate("erp_sync_failed"), { variant: "error" });
      }
    } catch (error) {
      syncProgress.resolve(null);
      enqueueSnackbar(translate("erp_sync_failed"), { variant: "error" });
    } finally {
      dispatch(setOdooSyncStatus("idle"));
    }
  };

  const handleDisconnect = async () => {
    try {
      const res = await disconnectErp();
      if (res && res.ok) {
        dispatch(disconnectOdooAction());
        enqueueSnackbar(translate("erp_disconnected"), { variant: "success" });
      } else {
        enqueueSnackbar(translate("erp_disconnect_failed"), {
          variant: "error",
        });
      }
    } catch (error) {
      enqueueSnackbar(translate("erp_disconnect_failed"), { variant: "error" });
    }
  };

  const handleConnectStart = (retryConnect: () => void) => {
    syncProgress.start(true, retryConnect);
  };

  const handleConnectResult = (
    entities: ISyncedEntity[] | null,
    errorKeys?: string[],
  ) => {
    syncProgress.resolve(entities, errorKeys);
  };

  return (
    <Stack sx={{ flex: 1, minHeight: 0 }}>
      <Typography
        variant="caption"
        sx={{
          color: palette.primary.main,
          fontWeight: 600,
          fontSize: "12.5px",
          letterSpacing: "normal",
          px: 1.25,
          pt: 1,
          pb: 1,
          textTransform: "none",
        }}
      >
        {translate("erp_connections")}
      </Typography>
      <Box
        sx={{
          borderBottom: "1px solid",
          borderColor: "text.borderLine",
          mb: 1,
        }}
      />

      <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", px: 1.25 }}>
        <AskDostOdooConnectorRow
          palette={palette}
          translate={translate}
          odoo={odoo}
          isSyncing={isSyncing}
          onConnect={() => setConnectOpen(true)}
          onSync={handleSync}
          onDisconnect={() => setDisconnectDialogOpen(true)}
        />

        <Typography
          variant="caption"
          sx={{
            display: "block",
            px: 0.5,
            pt: 1.5,
            color: palette.text.label,
            fontSize: "11.5px",
            lineHeight: 1.5,
            fontWeight: 400,
          }}
        >
          {translate("more_erp_connectors_coming")}
        </Typography>
      </Box>

      <AskDostOdooConnectDialog
        open={connectOpen}
        onClose={() => setConnectOpen(false)}
        translate={translate}
        onConnectStart={handleConnectStart}
        onConnectResult={handleConnectResult}
      />

      <AskDostSyncProgressModal
        open={syncProgress.open}
        isFirstConnect={syncProgress.isFirstConnect}
        rows={syncProgress.rows}
        isRunning={syncProgress.isRunning}
        hasError={syncProgress.hasError}
        onRetry={syncProgress.retry}
        onClose={syncProgress.close}
        palette={palette}
        translate={translate}
      />

      <ConfirmDialog
        open={disconnectDialogOpen}
        title={translate("disconnect_confirm_title")}
        content={translate("disconnect_confirm_message")}
        hideIcon
        onClose={() => setDisconnectDialogOpen(false)}
        onConfirm={() => {
          handleDisconnect();
          setDisconnectDialogOpen(false);
        }}
      />
    </Stack>
  );
};
